/**
 * Custom hook for high-precision stopwatch functionality
 * Uses requestAnimationFrame for smooth updates
 */
import { useState, useEffect, useRef, useCallback } from 'react'

export function useStopwatch(autoStart = false) {
  const [elapsedTime, setElapsedTime] = useState(0) // milliseconds
  const [isRunning, setIsRunning] = useState(autoStart)
  const startTimeRef = useRef(null)
  const animationFrameRef = useRef(null)

  /**
   * Start the stopwatch
   */
  const start = useCallback(() => {
    if (isRunning) return
    startTimeRef.current = Date.now() - elapsedTime
    setIsRunning(true)
  }, [isRunning, elapsedTime])

  /**
   * Stop the stopwatch
   */
  const stop = useCallback(() => {
    if (!isRunning) return
    setIsRunning(false)
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }
  }, [isRunning])

  /**
   * Reset the stopwatch to 0
   */
  const reset = useCallback(() => {
    setElapsedTime(0)
    startTimeRef.current = null
    setIsRunning(false)
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }
  }, [])

  /**
   * Format elapsed time into various formats
   */
  const formatTime = useCallback((ms) => {
    const totalSeconds = Math.floor(ms / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    const milliseconds = Math.floor((ms % 1000) / 10)

    return {
      hours: hours.toString().padStart(2, '0'),
      minutes: minutes.toString().padStart(2, '0'),
      seconds: seconds.toString().padStart(2, '0'),
      milliseconds: milliseconds.toString().padStart(2, '0'),
      formatted: `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
      formattedWithMs: `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds
        .toString()
        .padStart(2, '0')}`,
    }
  }, [])

  // Animation loop for updating elapsed time
  useEffect(() => {
    if (!isRunning) return undefined

    const updateTime = () => {
      if (startTimeRef.current) {
        setElapsedTime(Date.now() - startTimeRef.current)
      }
      animationFrameRef.current = requestAnimationFrame(updateTime)
    }

    animationFrameRef.current = requestAnimationFrame(updateTime)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [isRunning])

  // Auto-start if specified
  useEffect(() => {
    if (autoStart && !isRunning) {
      start()
    }
  }, [autoStart, isRunning, start])

  return {
    elapsedTime,
    isRunning,
    start,
    stop,
    reset,
    formatTime: () => formatTime(elapsedTime),
  }
}
